import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";
import { atlasRoutes } from "@/app/router/routes";
import { Box, Card, Heading, Text, Flex } from "@radix-ui/themes";
import { getFileDownloadUrl } from "../../uploaded-files/utils";

export const WebClipsViewPage: FC = () => {
  const { webClipId } = useParams<{ webClipId: Uuid }>();
  const navigate = useNavigate();

  if (!webClipId) return <div>Invalid ID</div>;

  const result = useQuery({
    queryKey: ["web-clip", webClipId],
    queryFn: () =>
      api.get("/internal/v1/web-clips/{web_clip_id}", {
        params: { path: { web_clip_id: webClipId } },
      }),
  });

  // Fetch uploaded file details if there's an image
  const imageFileId = result.data?.data?.clipped_image_uploaded_file_id;
  const imageFileResult = useQuery({
    queryKey: ["uploaded-file", imageFileId],
    queryFn: () =>
      api.get("/internal/v1/uploaded-files/{uploaded_file_id}", {
        params: { path: { uploaded_file_id: imageFileId! } },
      }),
    enabled: !!imageFileId,
  });

  const handleDelete = async () => {
    await api.delete("/internal/v1/web-clips/{web_clip_id}", {
      params: { path: { web_clip_id: webClipId } },
    });
    navigate(atlasRoutes.webClips({}).path);
  };

  if (result.isLoading) return <div>Loading...</div>;
  if (result.error) return <div>Error loading web clip</div>;
  if (!result.data?.data) return <div>Web clip not found</div>;

  const webClip = result.data.data;
  const imageFile = imageFileResult.data?.data;

  return (
    <Box p="4">
      <Heading size="6" mb="4">
        View Web Clip
      </Heading>
      <Card mb="4">
        <Flex direction="column" gap="3">
          <Box>
            <Text size="2" color="gray">
              Web Clip ID:
            </Text>
            <Text
              size="3"
              weight="bold"
              style={{
                fontFamily: "monospace",
                userSelect: "all",
                paddingLeft: "8px",
                fontStyle: "italic",
              }}
            >
              {webClip.id}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Space Account ID:
            </Text>
            <Text size="3" style={{ fontFamily: "monospace" }}>
              {" "}
              {webClip.associated_space_account_id}
            </Text>
          </Box>
          {webClip.timestamp && (
            <Box>
              <Text size="2" color="gray">
                Timestamp:
              </Text>
              <Text size="3"> {new Date(webClip.timestamp).toLocaleString()}</Text>
            </Box>
          )}
          <Box>
            <Text size="2" color="gray">
              Created At:
            </Text>
            <Text size="3"> {new Date(webClip.created_at).toLocaleString()}</Text>
          </Box>
          {webClip.captured_website_id && (
            <Box>
              <Text size="2" color="gray">
                Captured Website ID:
              </Text>
              <Text size="3" style={{ fontFamily: "monospace" }}>
                {" "}
                {webClip.captured_website_id}
              </Text>
            </Box>
          )}
        </Flex>
      </Card>

      {imageFile && (
        <Card mb="4">
          <Flex direction="column" gap="2">
            <Text size="2" color="gray">
              Clipped Image:
            </Text>
            <Box>
              <img
                src={getFileDownloadUrl(
                  imageFile.uploaded_file_bucket_name,
                  imageFile.uploaded_file_blob_name
                )}
                alt="Clipped image"
                style={{
                  maxWidth: "400px",
                  maxHeight: "400px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-a6)",
                  display: "block",
                }}
              />
            </Box>
          </Flex>
        </Card>
      )}
      <Flex gap="3" mb="4">
        {webClip.captured_website_id && (
          <Button
            size="2"
            variant="soft"
            onClick={() =>
              navigate(
                atlasRoutes.capturedWebsitesView({
                  params: { capturedWebsiteId: webClip.captured_website_id! },
                }).path
              )
            }
          >
            View Captured Website
          </Button>
        )}
        {webClip.clipped_image_uploaded_file_id && (
          <Button
            size="2"
            variant="soft"
            onClick={() =>
              navigate(
                atlasRoutes.uploadedFilesView({
                  params: { uploadedFileId: webClip.clipped_image_uploaded_file_id! },
                }).path
              )
            }
          >
            View Image File
          </Button>
        )}
        <Button size="2" color="red" onClick={handleDelete}>
          Delete
        </Button>
      </Flex>
      <details>
        <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
          <Text size="2" color="gray">
            Raw JSON Data
          </Text>
        </summary>
        <pre
          style={{
            fontSize: "12px",
            background: "var(--gray-a3)",
            padding: "12px",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {JSON.stringify(webClip, null, 2)}
        </pre>
      </details>
    </Box>
  );
};
